package com.ijoin.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.LegalDocumentType
import com.ijoin.i18n.strings

/**
 * Checkbox component for accepting legal documents.
 * Shows clickable links for each document type.
 */
@Composable
fun LegalCheckbox(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    onDocumentClick: (LegalDocumentType) -> Unit,
    documentTypes: List<LegalDocumentType> = LegalDocumentType.REGISTRATION_TYPES,
    enabled: Boolean = true,
    modifier: Modifier = Modifier
) {
    val strings = strings()

    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = onCheckedChange,
            enabled = enabled
        )

        Spacer(modifier = Modifier.width(8.dp))

        // Build text with clickable links as a column list
        Column(
            modifier = Modifier.weight(1f)
        ) {
            Text(
                text = strings.iAgreeToThe,
                style = MaterialTheme.typography.bodyMedium
            )

            Column(
                modifier = Modifier.padding(top = 4.dp, start = 8.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                documentTypes.forEach { type ->
                    val documentName = when (type) {
                        LegalDocumentType.TERMS_OF_SERVICE -> strings.termsOfService
                        LegalDocumentType.PRIVACY_POLICY -> strings.privacyPolicy
                        LegalDocumentType.REFUND_POLICY -> strings.refundPolicy
                        LegalDocumentType.ORGANIZER_TERMS -> strings.organizerTerms
                        LegalDocumentType.AI_DATA_CONSENT -> strings.aiDataConsent
                    }

                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            text = "• ",
                            style = MaterialTheme.typography.bodyMedium
                        )
                        Text(
                            text = documentName,
                            style = MaterialTheme.typography.bodyMedium.copy(
                                color = MaterialTheme.colorScheme.primary,
                                textDecoration = TextDecoration.Underline
                            ),
                            modifier = Modifier.clickable(enabled = enabled) {
                                onDocumentClick(type)
                            }
                        )
                    }
                }
            }
        }
    }
}

/**
 * Single document checkbox (for organizer terms).
 */
@Composable
fun SingleDocumentCheckbox(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    onDocumentClick: () -> Unit,
    documentType: LegalDocumentType,
    enabled: Boolean = true,
    modifier: Modifier = Modifier
) {
    val strings = strings()
    val documentName = when (documentType) {
        LegalDocumentType.TERMS_OF_SERVICE -> strings.termsOfService
        LegalDocumentType.PRIVACY_POLICY -> strings.privacyPolicy
        LegalDocumentType.REFUND_POLICY -> strings.refundPolicy
        LegalDocumentType.ORGANIZER_TERMS -> strings.organizerTerms
        LegalDocumentType.AI_DATA_CONSENT -> strings.aiDataConsent
    }

    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Checkbox(
            checked = checked,
            onCheckedChange = onCheckedChange,
            enabled = enabled
        )

        Spacer(modifier = Modifier.width(8.dp))

        Row(modifier = Modifier.weight(1f)) {
            Text(
                text = "${strings.iAgreeToThe} ",
                style = MaterialTheme.typography.bodyMedium
            )
            Text(
                text = documentName,
                style = MaterialTheme.typography.bodyMedium.copy(
                    color = MaterialTheme.colorScheme.primary,
                    textDecoration = TextDecoration.Underline
                ),
                modifier = Modifier.clickable(enabled = enabled) {
                    onDocumentClick()
                }
            )
        }
    }
}
